import React, { useState, useMemo, useCallback } from 'react';
import { useThemeMode } from '../../ThemeContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { 
  Box, 
  IconButton, 
  Tooltip
} from '@mui/material';
import {
  Edit as EditIcon
} from '@mui/icons-material';
import { searchDocuments, formatDate, getFileType, downloadFile } from '../../helpers/UtilsFunctions';
import { dataAPI } from '../../helpers/api_service';
import TrashIcon from '../../assets/Trash.svg';
import DownloadIcon from '../../assets/Download.svg';
import ProhibitedIcon from '../../assets/Prohibited.svg';
import { DataTable, AlertDialog } from '../Common';

const DocumentList = ({ 
  documents, 
  onDeleteDocument, 
  onEditDocument,
  onDownloadDocument,
  searchTerm: externalSearchTerm, 
  onSearchChange: externalOnSearchChange,
  showSearchOnly = false 
}) => {
  const { mode } = useThemeMode();
  const { hasEditPermission, hasDeletePermission } = usePermissions();
  const [internalSearchTerm, setInternalSearchTerm] = useState('');
  const [downloadingFiles, setDownloadingFiles] = useState(new Set());
  const [deleteDialog, setDeleteDialog] = useState({
    isOpen: false,
    documentId: null,
    documentName: ''
  });

  // Use external search term if provided, otherwise use internal state
  const searchTerm = externalSearchTerm !== undefined ? externalSearchTerm : internalSearchTerm;
  const setSearchTerm = externalOnSearchChange || setInternalSearchTerm;

  // Delete handlers
  const handleDeleteClick = useCallback((document) => {
    setDeleteDialog({
      isOpen: true,
      documentId: document.id,
      documentName: document.name
    });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteDialog.documentId) return;

    try {
      await dataAPI.deleteDocument(deleteDialog.documentId);
      // Call the parent's delete handler to update the UI
      if (onDeleteDocument) {
        onDeleteDocument(deleteDialog.documentId);
      }
      setDeleteDialog({ isOpen: false, documentId: null, documentName: '' });
    } catch (error) {
      console.error('Error deleting document:', error);
      // You might want to show an error message here
      setDeleteDialog({ isOpen: false, documentId: null, documentName: '' });
    }
  }, [deleteDialog.documentId, onDeleteDocument]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialog({ isOpen: false, documentId: null, documentName: '' });
  }, []);

  // Memoize columns to prevent recreation on every render
  const columns = useMemo(() => [
    {
      accessorKey: 'name',
      header: 'Document Name',
      size: 150,
      minSize: 120,
      maxSize: 200,
      Cell: ({ row }) => (
        <span style={{ 
          color: mode === 'dark' ? '#FFFFFF' : '#333333',
          fontSize: '14px',
          fontFamily: 'Urbanist, Arial, sans-serif',
          wordWrap: 'break-word',
          whiteSpace: 'normal',
          lineHeight: '1.4'
        }}>
          {row.original.name}
        </span>
      ),
    },
    {
      accessorKey: 'expiry_date',
      header: 'Expiry Date',
      size: 100,
      minSize: 90,
      maxSize: 110,
      Cell: ({ row }) => (
        <span style={{ 
          color: mode === 'dark' ? '#FFFFFF' : '#333333',
          fontSize: '14px',
          fontFamily: 'Urbanist, Arial, sans-serif'
        }}>
          {row.original.expiry_date ? formatDate(row.original.expiry_date) : '-'}
        </span>
      ),
    },
    {
      accessorKey: 'version',
      header: 'Version',
      size: 70,
      minSize: 60,
      maxSize: 80,
      Cell: ({ row }) => (
        <span style={{ 
          color: mode === 'dark' ? '#FFFFFF' : '#333333',
          fontSize: '14px',
          fontFamily: 'Urbanist, Arial, sans-serif'
        }}>
          {row.original.version || '-'}
        </span>
      ),
    },
    {
      accessorKey: 'file_path',
      header: 'File Type',
      size: 70,
      minSize: 60,
      maxSize: 80,
      Cell: ({ row }) => (
        <span style={{ 
          color: mode === 'dark' ? '#FFFFFF' : '#333333',
          fontSize: '14px',
          fontFamily: 'Urbanist, Arial, sans-serif'
        }}>
          {getFileType(row.original.file_path)}
        </span>
      ),
    },
    {
      accessorKey: 'updated_at',
      header: 'Uploaded Date',
      size: 100,
      minSize: 90,
      maxSize: 110,
      Cell: ({ row }) => (
        <span style={{ 
          color: mode === 'dark' ? '#FFFFFF' : '#333333',
          fontSize: '14px',
          fontFamily: 'Urbanist, Arial, sans-serif'
        }}>
          {formatDate(row.original.updated_at)}
        </span>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 100,
      minSize: 80,
      maxSize: 120,
      Cell: ({ row }) => {
        const status = row.original.status;
        const processingStatus = row.original.processing_status;
        
        // Show processing state while backend is working
        if (processingStatus === 'pending') {
          const color = '#ff9800';
          return (
            <span style={{ 
              color,
              fontSize: '14px',
              fontFamily: 'Urbanist, Arial, sans-serif',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              whiteSpace: 'normal'
            }}>
              <span style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: color,
                animation: 'pulse 1.5s ease-in-out infinite'
              }} />
              Processing...
            </span>
          );
        }

        if (processingStatus === 'failed') {
          const color = '#f44336';
          return (
            <span style={{ 
              color,
              fontSize: '14px',
              fontFamily: 'Urbanist, Arial, sans-serif',
              fontWeight: '500'
            }}>
              Processing Failed
            </span>
          );
        }

        let color = '#4caf50'; // Active - green
        if (status === 'Expiring Soon') color = '#ff9800';
        if (status === 'Expired') color = '#f44336';
        
        return (
          <span style={{ 
            color,
            fontSize: '14px',
            fontFamily: 'Urbanist, Arial, sans-serif',
            fontWeight: '500',
            wordWrap: 'break-word',
            whiteSpace: 'normal'
          }}>
            {status}
          </span>
        );
      },
    },
    // Removed processing_status column; rows will be disabled when pending
    {
      accessorKey: 'actions',
      header: 'Actions',
      size: 80,
      minSize: 70,
      maxSize: 90,
      enableSorting: false,
      Cell: ({ row }) => {
        const status = row.original.status;
        const processingStatus = row.original.processing_status;
        const isExpired = status === 'Expired';
        const isProcessingPending = processingStatus === 'pending';
        const isDisabled = isProcessingPending || downloadingFiles.has(row.original.id);
        
        return (
          <Box sx={{ 
            display: 'flex', 
            gap: 0.5,
            opacity: isProcessingPending ? 0.5 : 1,
            transition: 'opacity 0.3s ease'
          }}>
            <Tooltip title={isProcessingPending ? "Document is still being processed" : "Download"}>
              <IconButton 
                size="small" 
                sx={{ color: '#26C3C0' }} 
                disabled={isDisabled}
                onClick={async () => {
                  if (isProcessingPending) return;
                  const fileId = row.original.id;
                  setDownloadingFiles(prev => new Set(prev).add(fileId));
                  try {
                    if (onDownloadDocument) {
                      await onDownloadDocument(row.original);
                    } else {
                      await downloadFile(row.original.file_path, row.original.name);
                    }
                  } catch (error) {
                    console.error('Download failed:', error);
                  } finally {
                    setDownloadingFiles(prev => {
                      const newSet = new Set(prev);
                      newSet.delete(fileId);
                      return newSet;
                    });
                  }
                }}
              >
                <img 
                  src={DownloadIcon} 
                  alt="Download" 
                  style={{ 
                    width: '20px', 
                    height: '20px',
                    opacity: isDisabled ? 0.5 : 1,
                    transition: 'opacity 0.2s ease'
                  }} 
                />
              </IconButton>
            </Tooltip>
            {hasEditPermission('document') && (
              <Tooltip title={isProcessingPending ? "Document is still being processed" : (isExpired ? "Edit Expired Document" : "Edit")}>
                <IconButton 
                  size="small" 
                  sx={{ color: isExpired ? '#f44336' : '#26C3C0' }}
                  disabled={isProcessingPending}
                  onClick={() => {
                    if (isProcessingPending) return;
                    onEditDocument && onEditDocument(row.original);
                  }}
                >
                  {isExpired ? (
                    <img src={ProhibitedIcon} alt="Edit" style={{ width: '20px', height: '20px' }} />
                  ) : (
                    <EditIcon fontSize="small" style={{ width: '20px', height: '20px', color: '#26C3C0' }} />
                  )}
                </IconButton>
              </Tooltip>
            )}
            {hasDeletePermission('document') && (
              <Tooltip title={isProcessingPending ? "Document is still being processed" : "Delete"}>
                <IconButton 
                  size="small" 
                  sx={{ color: '#f44336' }}
                  disabled={isProcessingPending}
                  onClick={() => {
                    if (isProcessingPending) return;
                    handleDeleteClick(row.original);
                  }}
                >
                  <img src={TrashIcon} alt="Delete" style={{ width: '20px', height: '20px' }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ], [mode, onDeleteDocument, onEditDocument, onDownloadDocument, downloadFile, downloadingFiles, handleDeleteClick, hasEditPermission, hasDeletePermission]);

  return (
    <div className="documents-list-wrapper">
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
        `}
      </style>
      <DataTable
        data={documents}
        columns={columns}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        showSearchOnly={showSearchOnly}
        searchPlaceholder="Search documents..."
        searchFunction={searchDocuments}
        emptyMessage="No documents found"
        enableRowSelection={false}
        enableSorting={true}
        showHorizontalScroll={true}
        shadow={false}
        borderRadius="0"
        customStyles={{
          boxShadow: 'none',
          borderRadius: '0',
          '& .MuiTableRow-root': {
            borderRadius: '0 !important',
          },
          '& .MuiTableCell-root': {
            borderRadius: '0 !important',
          },
          '& .MuiTableRow-root:last-child': {
            borderRadius: '0 !important',
            '& .MuiTableCell-root': {
              borderRadius: '0 !important',
            }
          }
        }}
        getRowStyle={(row) => ({
          opacity: row.processing_status === 'pending' ? 0.6 : 1,
          backgroundColor: row.processing_status === 'pending'
            ? (mode === 'dark' ? 'rgba(255, 152, 0, 0.06)' : 'rgba(255, 152, 0, 0.04)')
            : 'transparent',
          filter: row.processing_status === 'pending' ? 'grayscale(0.2)' : 'none',
          transition: 'all 0.25s ease'
        })}
        getRowDisabled={(row) => row.processing_status === 'pending'}
      />
      <AlertDialog
        isOpen={deleteDialog.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        type="delete"
        title="Delete Document"
        message="Are you sure you want to delete"
        itemName={deleteDialog.documentName}
        confirmText="Delete"
        icon={TrashIcon}
      />
    </div>
  );
};

export default DocumentList;